import React, { useState, createContext, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// ─── Auth Context ─────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('fdv_token') || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setUser(data);
        else { localStorage.removeItem('fdv_token'); setToken(''); }
      })
      .catch(() => { localStorage.removeItem('fdv_token'); setToken(''); })
      .finally(() => setLoading(false));
  }, [token]);

  const login = (t, u) => {
    localStorage.setItem('fdv_token', t);
    setToken(t);
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem('fdv_token');
    setToken('');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// ─── RequireAuth — wrapper para rotas protegidas ──────────────────────────────

export const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/entrar', { state: { from: location.pathname }, replace: true });
    }
  }, [user, loading, navigate, location]);

  if (loading) return <FullLoader />;
  if (!user) return null;
  return children;
};

const FullLoader = () => (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f8fafc'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🧁</div>
      <div style={{ color: '#334155', fontSize: '0.85rem' }}>Carregando...</div>
    </div>
  </div>
);

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(124, 58, 237, 0.12);
  width: 100%;
  max-width: 400px;
  padding: 2rem;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 1.75rem;
  div:first-child { font-size: 2.5rem; margin-bottom: 0.25rem; }
  h1 {
    font-size: 1.3rem;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 0.2rem;
  }
  p {
    font-size: 0.8rem;
    color: #9ca3af;
    margin: 0;
  }
`;

const Tabs = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 3px;
  margin-bottom: 1.5rem;
`;

const Tab = styled.button`
  flex: 1;
  padding: 0.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.83rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${p => p.active ? 'white' : 'transparent'};
  color: ${p => p.active ? '#334155' : '#6b7280'};
  box-shadow: ${p => p.active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none'};
`;

const Field = styled.div`
  margin-bottom: 1rem;
  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.35rem;
  }
  input {
    width: 100%;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    padding: 0.7rem 0.9rem;
    font-size: 0.9rem;
    color: #1f2937;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;
    &:focus { border-color: #334155; }
    &::placeholder { color: #d1d5db; }
  }
`;

const SubmitBtn = styled(motion.button)`
  width: 100%;
  padding: 0.8rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #334155, #475569);
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 0.5rem;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  color: #dc2626;
  font-size: 0.8rem;
  margin-bottom: 1rem;
`;

const Divider = styled.div`
  text-align: center;
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 1.25rem 0 0;
`;

// ─── Login Page Component ─────────────────────────────────────────────────────

export default function Login() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', storeName: '', ownerName: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'register' && form.password !== form.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const body = mode === 'login'
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, storeName: form.storeName, ownerName: form.ownerName };

      const r = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Erro ao entrar'); setLoading(false); return; }

      login(data.token, data.user);
      navigate(from, { replace: true });
    } catch {
      setError('Não foi possível conectar ao servidor');
    }
    setLoading(false);
  };

  return (
    <Page>
      <Card
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Logo>
          <div>✂️</div>
          <h1>Magzy</h1>
          <p>Seu salão no próximo nível</p>
        </Logo>

        <Tabs>
          <Tab active={mode === 'login'} onClick={() => { setMode('login'); setError(''); }}>
            Entrar
          </Tab>
          <Tab active={mode === 'register'} onClick={() => { setMode('register'); setError(''); }}>
            Criar conta
          </Tab>
        </Tabs>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {mode === 'register' && (
              <motion.div
                key="register-fields"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <Field>
                  <label>Nome do salão / estúdio *</label>
                  <input
                    placeholder="Ex: Studio Ana Lima"
                    value={form.storeName}
                    onChange={e => set('storeName', e.target.value)}
                    required
                  />
                </Field>
                <Field>
                  <label>Seu nome</label>
                  <input
                    placeholder="Ex: Ana Lima (profissional responsável)"
                    value={form.ownerName}
                    onChange={e => set('ownerName', e.target.value)}
                  />
                </Field>
              </motion.div>
            )}
          </AnimatePresence>

          <Field>
            <label>E-mail *</label>
            <input
              type="email"
              placeholder="seu@email.com"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              required
              autoComplete="email"
            />
          </Field>

          <Field>
            <label>Senha *</label>
            <input
              type="password"
              placeholder={mode === 'register' ? 'Mínimo 6 caracteres' : '••••••••'}
              value={form.password}
              onChange={e => set('password', e.target.value)}
              required
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </Field>

          <AnimatePresence mode="wait">
            {mode === 'register' && (
              <motion.div
                key="confirm-field"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: 'hidden' }}
              >
                <Field>
                  <label>Confirmar senha *</label>
                  <input
                    type="password"
                    placeholder="Repita a senha"
                    value={form.confirmPassword}
                    onChange={e => set('confirmPassword', e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </Field>
              </motion.div>
            )}
          </AnimatePresence>

          <SubmitBtn
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? '...' : mode === 'login' ? 'Entrar' : 'Criar minha conta'}
          </SubmitBtn>
        </form>

        <Divider>
          {mode === 'login'
            ? 'Ainda não tem conta? Clique em "Criar conta" acima'
            : 'Já tem perfil? Clique em "Entrar" acima'}
        </Divider>
      </Card>
    </Page>
  );
}
